<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("pricing.configure_payment_gateway_title") }}</template>
        <template v-slot:body>
            <div v-if="initDone">
                <GeneralConfigurationView v-if="view === VIEW_GENERAL_CONFIGURATION" />
                <Przelewy24ConfigurationView v-if="view === VIEW_P24_CONFIGURATION" />
                <StripeConfigurationView v-if="view === VIEW_STRIPE_CONFIGURATION" />
                <AsaasConfigurationView v-if="view === VIEW_ASAAS_CONFIGURATION" />
                <ChooseExistingConfigurationView v-if="view === VIEW_CHOOSE_EXISTING" />
            </div>
            <div v-else class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="confirmInProgress" class="button is-primary" v-if="!!chosenConfig?.id" @click="confirm">
                    {{ translate("common.ok") }}
                </LoadingButton>

                <button class="button is-info" v-if="view === VIEW_GENERAL_CONFIGURATION && !chosenConfig?.id" @click="switchToUseExisting">
                    <span>{{ translate("pricing.use_existing_p24") }}</span>
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import GeneralConfigurationView from "./GeneralConfigurationView.vue";
    import Przelewy24ConfigurationView from "./Przelewy24ConfigurationView.vue";
    import StripeConfigurationView from "./StripeConfigurationView.vue";
    import ChooseExistingConfigurationView from "./ChooseExistingConfigurationView.vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import pricingApi from "../../Api/pricing";
    import { usePaymentGatewayConfigStore, usePricingSetupStore } from "../pricingSetupApp";
    import AsaasConfigurationView from "./AsaasConfigurationView.vue";

    const VIEW_GENERAL_CONFIGURATION = "general_configuration";

    const VIEW_P24_CONFIGURATION = "p24_configuration";
    const VIEW_STRIPE_CONFIGURATION = "stripe_configuration";
    const VIEW_ASAAS_CONFIGURATION = "asaas_configuration";

    const VIEW_CHOOSE_EXISTING = "choose_existing";

    const store = usePricingSetupStore();
    const paymentGatewayConfigStore = usePaymentGatewayConfigStore();

    const modal = ref<InstanceType<typeof RegularModal> | null>(null);
    const initDone = ref(false);
    const confirmInProgress = ref(false);

    const view = computed(() => paymentGatewayConfigStore.view);
    const chosenConfig = computed(() => paymentGatewayConfigStore.chosenConfig);
    const currenciesMap = computed(() => paymentGatewayConfigStore.currenciesMap);
    const eventId = computed(() => store.eventId);

    const setView = _view => {
        paymentGatewayConfigStore.view = _view;
    };

    const open = () => {
        initDone.value = false;
        paymentGatewayConfigStore.currenciesMap = {};
        paymentGatewayConfigStore.chosenConfig = null;

        setView("");

        modal.value?.open();

        return pricingApi
            .getConfiguredPaymentGateway(eventId.value)
            .then(configuredPaymentGatewayInfo => {
                setView(VIEW_GENERAL_CONFIGURATION);

                if (!configuredPaymentGatewayInfo.configId) {
                    return;
                }

                paymentGatewayConfigStore.chosenConfig = {
                    id: configuredPaymentGatewayInfo.configId,
                    name: configuredPaymentGatewayInfo.configName,
                    provider: configuredPaymentGatewayInfo.provider,
                };

                configuredPaymentGatewayInfo.currencies.forEach(currency => {
                    paymentGatewayConfigStore.currenciesMap[currency] = true;
                });
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    };

    const confirm = () => {
        confirmInProgress.value = true;
        const currenciesToPersist = Object.keys(currenciesMap.value).filter(curr => !!currenciesMap.value[curr]);

        if (!chosenConfig.value) {
            return;
        }

        return pricingApi
            .configurePaymentGateway(eventId.value, {
                currencies: currenciesToPersist,
                configId: chosenConfig.value.id,
            })
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    };

    const switchToUseExisting = () => {
        setView(VIEW_CHOOSE_EXISTING);
    };

    const close = () => {
        modal.value?.close();
    };

    defineExpose({ open });
</script>

<style scoped></style>
